import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Button, Grid, Skeleton, Stack } from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import { useAtom } from 'jotai'
import { IssueType, USER_EVENT_TYPES } from '@finance-lab/utils/types'
import { MinimumDistanceSlider } from './MinimumDistanceSlider'
import DropdownSelect from '../../components/DropdownSelect'
import DropdownTreeView, { DropdownTreeViewItems } from './DropdownTreeView'
import { IssueEntity } from '../../../types/IssueEntity'
import UserEventApi from '../../../api/UserEventApi'
import { getAdvisors, getSectors } from './utils/filters'
import ToggleIssueTypes from '../../components/ToggleIssueTypes'
import MarketplacesApi from '../../../api/MarketplacesApi'
import { Marketplace } from '../../../types/Marketplace'
import { issueFiltersAtom, IssueFilterType } from '../../../atoms/issueAtom'
import ToggleWatchLists from '../../components/ToggleWatchLists'
import { SelectDateRange } from '../../components/SelectDateRange'

type IssueFilters = typeof issueFiltersAtom

interface IssueFiltersProps {
    isLoading: boolean
    issues: IssueEntity[] | null
    offeredSizeMinValue?: number
}

const userEventApi = new UserEventApi()

const IssueFilters = ({ isLoading, issues, offeredSizeMinValue = 0 }: IssueFiltersProps) => {
    const [filters, setFilters] = useAtom(issueFiltersAtom)
    const { t, i18n } = useTranslation()

    const abbAlternatives = useMemo(
        () => [
            { id: 'all', label: t('pages.issues.filters.abbAll') },
            { id: 'only-abb', label: t('pages.issues.filters.abbOnly') },
            { id: 'exclude-abb', label: t('pages.issues.filters.abbExclude') },
        ],
        [t],
    )
    const warrantsAlternatives = useMemo(
        () => [
            { id: 'all', label: t('pages.issues.filters.warrantsAll') },
            { id: 'only-warrants', label: t('pages.issues.filters.warrantsOnly') },
            { id: 'exclude-warrants', label: t('pages.issues.filters.warrantsExclude') },
        ],
        [t],
    )
    const guarantorAlternatives = useMemo(
        () => [
            { id: 'all', label: t('pages.issues.filters.guarantorsAll') },
            { id: 'only-guarantors', label: t('pages.issues.filters.guarantorsOnly') },
            { id: 'no-guarantors', label: t('pages.issues.filters.guarantorsNone') },
        ],
        [t],
    )
    const issueTermsAlternatives = useMemo(
        () => [
            { id: 'all', label: t('pages.issues.filters.issueTermsAll') },
            { id: 'only-issue-terms', label: t('pages.issues.filters.issueTermsOnly') },
            { id: 'no-issue-terms', label: t('pages.issues.filters.issueTermsNone') },
        ],
        [t],
    )
    const { financialAdvisors, legalAdvisors } = useMemo(
        () => getAdvisors(issues || [], i18n.language),
        [issues, i18n.language],
    )
    const sectors = useMemo(() => getSectors(issues || []), [issues])
    const [allMarketplaces, setAllMarketplaces] = useState<Marketplace[]>([])
    const [filteredMarketplaces, setFilteredMarketplaces] = useState<DropdownTreeViewItems>({})
    const [marketplacesWithIssues, setMarketplacesWithIssues] = useState<string[]>([])
    const [showMoreFilters, setShowMoreFilters] = useState(false)

    useEffect(() => {
        const fetchMarkets = async () => {
            const api = new MarketplacesApi()
            const markets = await api.allMarketplaces()

            setAllMarketplaces(markets)
        }

        void fetchMarkets()
    }, [])

    useEffect(() => {
        if (!issues?.length) return

        const marketplaces = new Set<string>()

        issues.forEach((issue) => {
            marketplaces.add(issue.marketplaceId)
        })

        setMarketplacesWithIssues([...marketplaces])
    }, [issues])

    useEffect(() => {
        const groupedMarketLists = allMarketplaces
            .filter((marketplace) => !!marketplace.listName && marketplacesWithIssues.includes(marketplace.id))
            .reduce((acc, val) => {
                const key = val.marketplaceName
                const arr = acc[key] || []

                return {
                    ...acc,
                    [key]: [
                        ...arr,
                        {
                            id: val.id,
                            label: val.listName,
                        },
                    ],
                }
            }, {})

        setFilteredMarketplaces(groupedMarketLists)
    }, [allMarketplaces, marketplacesWithIssues])

    const handleFilterChange = useCallback(
        async (filterType: keyof IssueFilterType, selectedItems: boolean | (string | Date)[]) => {
            setFilters((prevFilters) => {
                if (prevFilters[filterType].isSingleChoice && Array.isArray(selectedItems)) {
                    return {
                        ...prevFilters,
                        [filterType]: {
                            ...prevFilters[filterType],
                            value: selectedItems.filter(
                                (i) => !(prevFilters[filterType].value as Array<unknown>).includes(i),
                            ),
                        },
                    }
                }

                return {
                    ...prevFilters,
                    [filterType]: { ...prevFilters[filterType], value: selectedItems },
                }
            })
            await userEventApi.logEvent(USER_EVENT_TYPES.FILTER_ISSUES, {
                filterType,
                selectedItems,
            })
        },
        [setFilters],
    )

    const handleIssueTypeFilterChange = useCallback(
        (v: IssueType[]) => handleFilterChange('issueTypes', v),
        [handleFilterChange],
    )

    return (
        <>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 3, sm: 0 }}>
                <Stack
                    sx={{
                        height: 116,
                        minWidth: 200,
                        pr: 3,
                        alignItems: 'center',
                    }}
                >
                    {isLoading && <Skeleton variant='rounded' width='100%' height='100%' />}
                    {!isLoading && (
                        <ToggleIssueTypes
                            disabledItems={filters.issueTypes.disabledItems}
                            onChange={(v) => void handleIssueTypeFilterChange(v)}
                            value={filters.issueTypes.value}
                            excludeTypes={['Warrant', 'Convertible']}
                        />
                    )}
                </Stack>
                <Grid
                    container
                    spacing={3}
                    sx={{
                        flexGrow: 1,
                        width: '100%',
                        maxWidth: '100%',
                    }}
                >
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        {isLoading && <Skeleton variant='rounded' width='100%' height='52px' />}
                        {!isLoading && (
                            <SelectDateRange
                                minDate='2020-01-01'
                                onChange={(values) => void handleFilterChange('dates', values)}
                                values={filters.dates.value}
                            />
                        )}
                    </Grid>
                    <Grid size={{ xs: 6, md: 3 }}>
                        {isLoading && <Skeleton variant='rounded' width='100%' height='52px' />}
                        {!isLoading && (
                            <DropdownSelect
                                label={t('pages.issues.filters.financialAdvisor')}
                                items={financialAdvisors}
                                multiple
                                selectedItems={filters.financialAdvisors.value}
                                onChange={(selectedItems) =>
                                    void handleFilterChange('financialAdvisors', selectedItems)
                                }
                            />
                        )}
                    </Grid>
                    <Grid size={{ xs: 6, md: 3 }}>
                        {isLoading && <Skeleton variant='rounded' width='100%' height='52px' />}
                        {!isLoading && (
                            <DropdownSelect
                                label={t('pages.issues.filters.legalAdvisor')}
                                items={legalAdvisors}
                                multiple
                                selectedItems={filters.legalAdvisors.value}
                                onChange={(selectedItems) => void handleFilterChange('legalAdvisors', selectedItems)}
                            />
                        )}
                    </Grid>
                    <Grid size={{ xs: 6, md: 3 }}>
                        {isLoading && <Skeleton variant='rounded' width='100%' height='52px' />}
                        {!isLoading && (
                            <DropdownTreeView
                                items={sectors}
                                label={t('entities.company.fields.industry')}
                                pluralLabel={t('site.filters.industries')}
                                selectedItems={filters.sectors.value}
                                onChange={(selectedItems) => void handleFilterChange('sectors', selectedItems)}
                            />
                        )}
                    </Grid>
                    <Grid size={{ xs: 6, md: 3 }}>
                        {isLoading && <Skeleton variant='rounded' width='100%' height='52px' />}
                        {!isLoading && (
                            <ToggleWatchLists
                                onChange={(v) => void handleFilterChange('onlyInWatchLists', v)}
                                value={filters.onlyInWatchLists.value}
                            />
                        )}
                    </Grid>
                    <Grid size={{ xs: 6, md: 3 }}>
                        {isLoading && <Skeleton variant='rounded' width='100%' height='52px' />}
                        {!isLoading && (
                            <MinimumDistanceSlider
                                label={t('pages.issues.filters.preMoney')}
                                allSelectedMessage={t('site.filters.allCompanies')}
                                value={filters.preMoney.value}
                                includeGreaterValues={filters.preMoney.includeGreaterValues as boolean}
                                valueLimits={[0, filters.preMoney.maxValue as number]}
                                onChange={(value, isIncludeGreaterValues) =>
                                    setFilters((prevFilters) => ({
                                        ...prevFilters,
                                        preMoney: {
                                            ...prevFilters.preMoney,
                                            value: value,
                                            hasMaxValueSet: isIncludeGreaterValues,
                                        },
                                    }))
                                }
                                unit='MSEK'
                                includeInputFields
                            />
                        )}
                    </Grid>
                    <Grid size={{ xs: 6, md: 3 }}>
                        {isLoading && <Skeleton variant='rounded' width='100%' height='52px' />}
                        {!isLoading && (
                            <MinimumDistanceSlider
                                label={t('pages.issues.filters.offeringSize')}
                                allSelectedMessage={t('site.filters.allIssues')}
                                value={[
                                    Math.max(filters.offeredSize.value[0], offeredSizeMinValue),
                                    filters.offeredSize.value[1],
                                ]}
                                includeGreaterValues={filters.offeredSize.includeGreaterValues as boolean}
                                valueLimits={[offeredSizeMinValue, filters.offeredSize.maxValue as number]}
                                onChange={(value, isIncludeGreaterValues) =>
                                    setFilters((prevFilters) => ({
                                        ...prevFilters,
                                        offeredSize: {
                                            ...prevFilters.offeredSize,
                                            value: value,
                                            hasMaxValueSet: isIncludeGreaterValues,
                                        },
                                    }))
                                }
                                unit='MSEK'
                                includeInputFields
                            />
                        )}
                    </Grid>
                    <Grid size={{ xs: 6, md: 3 }}>
                        {isLoading && <Skeleton variant='rounded' width='100%' height='52px' />}
                        {!isLoading && (
                            <DropdownTreeView
                                label={t('pages.issues.filters.marketplaces')}
                                pluralLabel={t('pages.issues.filters.marketplaces')}
                                items={filteredMarketplaces}
                                selectedItems={filters.marketplaces.value}
                                onChange={(selectedItems) => void handleFilterChange('marketplaces', selectedItems)}
                            />
                        )}
                    </Grid>
                    {showMoreFilters ? (
                        <>
                            <Grid size={{ xs: 6, md: 3 }}>
                                <DropdownSelect
                                    label={t('pages.issues.filters.unitIssues')}
                                    items={warrantsAlternatives}
                                    selectedItems={filters.warrants.value}
                                    onChange={(selectedItems) => void handleFilterChange('warrants', selectedItems)}
                                />
                            </Grid>
                            <Grid size={{ xs: 6, md: 3 }}>
                                <DropdownSelect
                                    disabled={!filters.issueTypes.value.includes('Rights issue')}
                                    label={t('pages.issues.filters.issueTerms')}
                                    items={issueTermsAlternatives}
                                    selectedItems={filters.issueTerms.value}
                                    onChange={(selectedItems) => void handleFilterChange('issueTerms', selectedItems)}
                                />
                            </Grid>
                            <Grid size={{ xs: 6, md: 3 }}>
                                <DropdownSelect
                                    label={t('pages.issues.filters.guarantors')}
                                    items={guarantorAlternatives}
                                    selectedItems={filters.guarantors.value}
                                    onChange={(selectedItems) => void handleFilterChange('guarantors', selectedItems)}
                                />
                            </Grid>
                            <Grid size={{ xs: 6, md: 3 }}>
                                <DropdownSelect
                                    disabled={!filters.issueTypes.value.includes('Directed issue')}
                                    label={t('pages.issues.filters.bookBuilding')}
                                    items={abbAlternatives}
                                    selectedItems={filters.abb.value}
                                    onChange={(selectedItems) => void handleFilterChange('abb', selectedItems)}
                                />
                            </Grid>
                            <Grid size={{ xs: 6, md: 3 }}>
                                <MinimumDistanceSlider
                                    label={t('pages.issues.filters.marketSizeToday')}
                                    allSelectedMessage={t('site.filters.allCompanies')}
                                    value={filters.marketSize.value}
                                    includeGreaterValues={filters.marketSize.includeGreaterValues as boolean}
                                    valueLimits={[0, filters.marketSize.maxValue as number]}
                                    onChange={(value, isIncludeGreaterValues) =>
                                        setFilters((prevFilters) => ({
                                            ...prevFilters,
                                            marketSize: {
                                                ...prevFilters.marketSize,
                                                value: value,
                                                hasMaxValueSet: isIncludeGreaterValues,
                                            },
                                        }))
                                    }
                                    unit='MSEK'
                                    includeInputFields
                                />
                            </Grid>
                            <Grid size={{ xs: 6, md: 3 }}>
                                <MinimumDistanceSlider
                                    label={t('pages.issues.filters.marketSizePrePublication')}
                                    allSelectedMessage={t('site.filters.allCompanies')}
                                    value={filters.marketSizePrePublication.value}
                                    includeGreaterValues={
                                        filters.marketSizePrePublication.includeGreaterValues as boolean
                                    }
                                    valueLimits={[0, filters.marketSizePrePublication.maxValue as number]}
                                    onChange={(value, isIncludeGreaterValues) =>
                                        setFilters((prevFilters) => ({
                                            ...prevFilters,
                                            marketSizePrePublication: {
                                                ...prevFilters.marketSizePrePublication,
                                                value: value,
                                                hasMaxValueSet: isIncludeGreaterValues,
                                            },
                                        }))
                                    }
                                    unit='MSEK'
                                    includeInputFields
                                />
                            </Grid>
                        </>
                    ) : null}
                </Grid>
            </Stack>
            <Box
                sx={{
                    textAlign: 'center',
                    py: 2,
                }}
            >
                <Button
                    disabled={isLoading}
                    onClick={() => setShowMoreFilters((prev) => !prev)}
                    endIcon={showMoreFilters ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                >
                    {isLoading && <Skeleton variant='rounded' width={150} />}
                    {!isLoading &&
                        (showMoreFilters
                            ? t('site.actions.hideAdvancedFilters')
                            : t('site.actions.showAdvancedFilters'))}
                </Button>
            </Box>
        </>
    )
}

export default IssueFilters
